import { defineStore, acceptHMRUpdate } from 'pinia'
import { apiClient } from '@/helpers/api.js'
import {
  INVENTORY_CARD_COLUMN_OPTIONS,
  INVENTORY_SORT_OPTIONS,
  AVAILABLE_FILTERS,
  BASE_PARAM_KEYS,
} from '@/constants'
import { useUserStore } from '@/stores/UserStore'
import { generateCardData } from "@/helpers/card.js"
import { getInventoryStatus, checkDismissedDuplication } from "@/helpers/inventory.js"
import { setToLocalStorageByUserId, getFromLocalStorageByUserId } from '@/helpers/general'
import { extractBaseParams, buildSearchParams } from '@/helpers/filters'
import { useConfigStore } from '@/stores/ConfigStore'
import { useCardStore } from '@/stores/CardStore'

export const useInventoryStore = defineStore('InventoryStore', {
  state: () => {
    return {
      columnOptions: [...INVENTORY_CARD_COLUMN_OPTIONS],
      sortOption: INVENTORY_SORT_OPTIONS[1], // Default: 'Recently added'
      inventoryDetailsLoader: false,
      fieldSearchResult: [],
      isInventory: false,
      nextCardId: null,
      previousCardId: null,
      currentFetchController: null,

      views: [],
      viewsLoading: false,
      selectedView: null,
      creatingView: false,
      editingView: false,
      openView: false,
    }
  },

  getters: {},

  actions: {
    // Initialize sortOption and columnOptions from localStorage
    initFromLocalStorage() {
      const userStore = useUserStore()
      const userId = userStore.user?.id

      // Init sortOption
      const savedSort = getFromLocalStorageByUserId('inventorySort', userId)
      if (savedSort) {
        this.sortOption = savedSort
      }

      // Init columnOptions
      const savedColumns = getFromLocalStorageByUserId('display_columns_inventory', userId)

      if (savedColumns) {
        this.columnOptions = savedColumns
      }
    },

    updateToDefault() {
      this.columnOptions = INVENTORY_CARD_COLUMN_OPTIONS
      this.sortOption = INVENTORY_SORT_OPTIONS[1]
    },

    updateColumnOption(option) {
      console.log('updateColumnOption', option)
      const userStore = useUserStore()
      this.columnOptions = this.columnOptions.reduce((acc, item) => {
        item.field === option.field ? acc.push({...item, checked: !item.checked}) :  acc.push(item)

        return acc
      }, [])

      const displayColumns = JSON.parse(localStorage.getItem("display_columns_inventory")) || {}
      localStorage.setItem("display_columns_inventory", JSON.stringify({ ...displayColumns, [userStore.user.id]: this.columnOptions }))
    },

    updateSortOption(option) {
      const userStore = useUserStore()
      this.sortOption = option
      setToLocalStorageByUserId(option, 'inventorySort', userStore.user.id)
    },

    async fetchInventory({ id, perPage = 25, isPagination = false, viewId = null }) {
      const cardStore = useCardStore()
      const userStore = useUserStore()

      try {
        if (viewId && !this.views.length) {
          const dataViews = await this.fetchViews()

          this.selectedView = dataViews.find(view => view.id === viewId)
          this.columnOptions = this.selectedView?.columns || INVENTORY_CARD_COLUMN_OPTIONS

          if (this.selectedView?.sort) {
            this.sortOption = this.selectedView.sort
          }
        }
        
        // Cancel previous request if it exists
        if (this.currentFetchController) {
          this.currentFetchController.abort()
        }

        // Create new AbortController for current request
        const controller = new AbortController()
        this.currentFetchController = controller

        cardStore.cardsLoading = true

        const perPage_ = getFromLocalStorageByUserId(`perPage_inventory`, userStore.user.id) || perPage

        if (cardStore.itemFilters.duplicate) {
          // Clear controller before return
          this.currentFetchController = null
          cardStore.hasDuplicates = true

          await this.fetchDuplicates()
          cardStore.duplicationPopup = cardStore.hasDuplicates ? checkDismissedDuplication(userStore.user.id) : false

          cardStore.cards = cardStore.duplicates
          
          return
        }

        const configStore = useConfigStore()  

        // let inventoryList = { data: { available_filters: AVAILABLE_FILTERS } }

        // delete cardStore.itemFilters.status

        let sortBy_ = ''

        if (!cardStore.itemFilters.sortBy) {
          const savedSort = JSON.parse(localStorage.getItem('inventorySort'))
  
          sortBy_ = savedSort && savedSort[userStore.user?.id] && savedSort[userStore.user?.id].value || 'created_at.desc'
        }

        // Extract base params from itemFilters using constants
        const { baseParams: extractedBaseParams, filterParams: remainingFilters } = extractBaseParams(
          cardStore.itemFilters, 
          BASE_PARAM_KEYS
        )

        const baseParams = {
          page: cardStore.page,
          perPage: perPage_,
          sortBy: extractedBaseParams.sortBy || sortBy_,
          ...extractedBaseParams,
        }

        // Combine remaining filters into a single filter parameter with comma separation
        const filterParams = Object.entries(remainingFilters).length 
        ? `filter=${Object.entries(remainingFilters)
            .map(([key, value]) => `${key}=${value}`)
            .join(',')}`
        : ''

        const url = `/inventory/${id}${filterParams ? '?' + filterParams : ''}${
          Object.keys(baseParams).length ? (filterParams ? '&' : '?') + buildSearchParams({
            ...baseParams,
            ...(cardStore.itemFilters.q ? { q: cardStore.itemFilters.q } : {})
          }) : ''
        }`

        // Add signal for request cancellation capability
        const { data } = await apiClient().get(url, {
          signal: controller.signal
        })

        // Check if request was canceled
        if (controller.signal.aborted) {
          return
        }

        if (data?.has_duplicates) {
          cardStore.hasDuplicates = data.has_duplicates
          cardStore.duplicationPopup = cardStore.hasDuplicates ? checkDismissedDuplication(userStore.user.id) : false
          cardStore.duplicatesLength = data.duplicate_count
          this.fetchDuplicates()
        }

        const updatedInventory = data?.items.reduce((acc, item) => {
          item.listing = item.listing || {}
          
          // Merge card and listing data, add status fields
          acc.push({ 
            ...item.card, 
            ...item.listing, 
            status: item.card.status, 
            inventoryStatus: getInventoryStatus(item.card),
            set: item.card.set,
            number: item.card.number,
            title: item.card.title,
            description: item.card.description,
            player: item.card.player,
            team: item.card.team,
            year: item.card.year,
            id: item.card.id,
            listingId: item.listing?.id || null,
            collxListedStatus: item.listing?.collx_status,
            ebayListedStatus: item.listing?.ebay_status,
            whatnotListedStatus: item.listing?.whatnot_status,
            shopifyListedStatus: item.listing?.shopify_status,
            sku: item.listing?.sku || item.card?.sku,
            quantity_available: item.listing ? item.listing.quantity_available : 0,
          })
          return acc
        }, [])

        const cards = isPagination 
          ? [...cardStore.cards, ...updatedInventory] 
          : updatedInventory

        const preparedCards = []

        cards.forEach((card, index) => {
          const cardData = generateCardData(card, cardStore.selectedRanges, configStore.config)
          preparedCards.push(cardData)
        })

        cardStore.cards = preparedCards

        cardStore.totalCards = data.total_items_original

        if (data.available_filters?.length || Object.keys(data.available_filters)?.length) {
          cardStore.availableFilters = {
            attribute: [ ...data.available_filters?.attribute ],
            brand: [ ...data.available_filters?.brand ],
            category: [ ...data.available_filters?.category ],
            graded: [ ...data.available_filters?.graded ],
            grader: [ ...data.available_filters?.grader ],
            team: [ ...data.available_filters?.team ],
            status: { ...data.available_filters?.status },
            year: [ ...data.available_filters?.year ],
            box: [ ...data.available_filters?.box ],
            row: [ ...data.available_filters?.row ],
            shelf: [ ...data.available_filters?.shelf ],
            cabinet: [ ...data.available_filters?.cabinet ],
            set: [ ...data.available_filters?.set ],
          }
        }

        cardStore.pagination = {
          perPage: perPage_,
          currentPage: data.current_page,
          totalItems: data.total_items,
          nextPage: data.next_page ? data.current_page + 1 : null,
          prevPage: data.current_page === 1 ? null : data.current_page - 1,
          totalPages: data.total_pages
        }

        setToLocalStorageByUserId(data.total_items, 'inventoryTotalItems', userStore.user?.id)

        cardStore.cardsLoading = false
        
        // Clear controller after successful completion
        this.currentFetchController = null
      } catch (error) {
        // Ignore error if it's a request cancellation
        if (error.name === 'AbortError' || error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
          console.log('Request was canceled - ignoring response')
          return
        }
        
        cardStore.cardsLoading = false
        console.error('fetchInventory error', error)
        
        // Clear controller on error
        this.currentFetchController = null
      }
    },

    async fetchInventoryById(cardId) {
      if (!cardId) return

      const userStore = useUserStore()
      const configStore = useConfigStore()
      const cardStore = useCardStore()

      try {
        this.inventoryDetailsLoader = true
        const { data } = await apiClient().get(`/inventory/${userStore.user.default_inventory_id}/${cardId}`)
        console.log('data', data)
        const card = { 
          ...data.card, 
          ...data.listing, 
          status: data.card.status, 
          inventoryStatus: getInventoryStatus(data.card),
          set: data.card.set,
          number: data.card.number,
          title: data.card.title,
          description: data.card.description,
          player: data.card.player,
          team: data.card.team,
          year: data.card.year,
          id: data.card.id,
          sku: data.card.sku,
          listingId: data.listing?.id || null,
          collxListedStatus: data.listing?.collx_status,
          ebayListedStatus: data.listing?.ebay_status,
          whatnotListedStatus: data.listing?.whatnot_status,
          shopifyListedStatus: data.listing?.shopify_status,
          sku: data.listing?.sku || data.card?.sku,
          quantity_available: data.listing ? data.listing.quantity_available : 0,
          collx_type_id: data.card.collx_type_id,
          sport: data.card.sport,
        }

        cardStore.selectedCardData = generateCardData(card, cardStore.selectedRanges, configStore.config)

        this.inventoryDetailsLoader = false

        this.fetchNextPrevIds()
      } catch (error) {
        this.inventoryDetailsLoader = false
        console.error('fetchInventoryById error', error)
      }
    },

    async updatedInventoryCard(cardId, withoutLoading = false) {
      try {
        this.fetchInventoryById(cardId, withoutLoading)
      } catch (error) {
        console.error('updatedInventoryCard error', error)
      }
    },

    async updatedInventory(body) {
      const configStore = useConfigStore()
      const cardStore = useCardStore()

      try {
        const { data } = await apiClient().patch(`/cards/${cardStore.selectedCardData.id}`, body) 
         
        const cardData = cardStore.selectedCardData ? { ...cardStore.selectedCardData, ...data } : { ...data}
        cardStore.selectedCardData = {
          ...generateCardData(cardData, cardStore.selectedRanges, configStore.config),
          inventoryStatus: getInventoryStatus(cardData),
          listingId: cardStore.selectedCardData.listingId,
        }
      } catch (error) {
        console.error('updatedInventory error', error)
      }
    },

    async updateMatchedCard(selectedMatch) {
      const configStore = useConfigStore()
      const cardStore = useCardStore()

      try {
        if (!configStore.config?.hasActiveSubscription) return

        const { data } = await apiClient().post(`/cards/${cardStore.selectedCardData.id}/update-match`, { ...selectedMatch })

        if (!data) return

        cardStore.selectedCardData = generateCardData(data, cardStore.selectedRanges, configStore.config)
      } catch (error) {
        console.log('updateMatchedCard error inventory', error)
      }
    },

    async fetchNextPrevIds() {
      const userStore = useUserStore()
      const cardStore = useCardStore()

      try {
        const { data } = await apiClient().get(`/inventory/${userStore.user.default_inventory_id}/prev-next/${cardStore.selectedCardData.id}`)

        const { next_card_id, prev_card_id, total_cards, current_index } = data

        this.nextCardId = next_card_id
        this.previousCardId = prev_card_id

        cardStore.totalCards = total_cards
        cardStore.cardIndex = current_index
      } catch (error) {
        console.log('fetchNextPrevIds error inventory', error)
      }
    },

    async fetchDuplicates(perPage = 25) {
      const userStore = useUserStore()
      const cardStore = useCardStore()
      const configStore = useConfigStore()

      try {
        const perPage_ = getFromLocalStorageByUserId(`perPage_inventory`, userStore.user.id) || perPage

        cardStore.cardsLoading = true

        const { data } = await apiClient().get(`/inventory/${userStore.user.default_inventory_id}/duplicates?perPage=${perPage_}&page=${cardStore.page}`)
        cardStore.duplicatesLength = data.total_items

        const updatedDuplicates = []
        data.items.forEach(item => {
          updatedDuplicates.push({
            id: item.collx_match_id,
            collx_match_id: item.collx_match_id,
            isHeader: true
          })

          item.cards.forEach((item, index) => {
            const preparedCard = { 
              ...item.card, 
              ...item.listing, 
              status: item.card.status, 
              inventoryStatus: getInventoryStatus(item.card),
              set: item.card.set,
              number: item.card.number,
              title: item.card.title,
              description: item.card.description,
              player: item.card.player,
              team: item.card.team,
              year: item.card.year,
              id: item.card.id,
              sku: item.card.sku,
              listingId: item.listing?.id || null,
              collxListedStatus: item.listing?.collx_status,
              ebayListedStatus: item.listing?.ebay_status,
              whatnotListedStatus: item.listing?.whatnot_status,
              shopifyListedStatus: item.listing?.shopify_status,
              sku: item.listing?.sku || item.card?.sku,
              quantity_available: item.listing ? item.listing.quantity_available : 0,
            }
            
            const cardData = generateCardData(preparedCard, cardStore.selectedRanges, configStore.config)
            updatedDuplicates.push(cardData)
          })
        })

        // cardStore.pagination = {
        //   perPage: perPage_,
        //   currentPage: data.current_page,
        //   totalItems: data.total_items,
        //   nextPage: data.next_page ? data.current_page + 1 : null,
        //   prevPage: data.current_page === 1 ? null : data.current_page - 1,
        //   totalPages: data.total_pages
        // }

        cardStore.duplicates = updatedDuplicates

        cardStore.cardsLoading = false
      } catch (error) {
        console.error('fetchDuplicates error', error)
        cardStore.cardsLoading = false
      }
    },

    async addCardsFromBatchToInventory(batchId) {
      const cardStore = useCardStore()
      const arrayOfIds = []

      for (let i = 0; i < cardStore.selectedRanges.length; i++) {
        cardStore.cards[cardStore.selectedRanges[i].startIndex]?.id ? arrayOfIds.push(cardStore.cards[cardStore.selectedRanges[i].startIndex].id) : arrayOfIds.push(cardStore.cards[0].id)
      }

      try {
        await apiClient().post(`/inventory/add-cards`, { cards: arrayOfIds })
      } catch (error) {
        console.error('addCardsFromBatchToInventory error', error)
      }
    },

    async fetchViews() {
      try {
        this.viewsLoading = true
        const { data } = await apiClient().get(`/user/saved-views`)
        this.views = data?.saved_views || []
        this.viewsLoading = false

        return this.views
      } catch (error) {
        this.viewsLoading = false
        console.error('fetchViews error', error)
      }
    },

    async createView(body) {
      const userStore = useUserStore()
      try {
        const { data } = await apiClient().post(`/user/saved-views`, { ...body, favorite: !this.views.length ? true : false })
        this.views = data?.saved_views || this.views
        userStore.user.saved_views = data?.saved_views || userStore.user.saved_views

        return this.views[this.views.length - 1]
      } catch (error) {
        console.error('createView error', error)
      }
    },

    async deleteView(viewId) {
      const userStore = useUserStore()
      try {
        const { data } = await apiClient().delete(`/user/saved-views/${viewId}`)
        this.views = data?.saved_views || this.views
        userStore.user.saved_views = data?.saved_views || userStore.user.saved_views

        return this.views
      } catch (error) {
        console.error('deleteView error', error)
      }
    },

    async updateView(viewId, body) {
      try {
        const { data } = await apiClient().put(`/user/saved-views/${viewId}`, body)
        this.views = data?.saved_views || this.views
        // Use the updated view from the response (has id), fall back to merging id into body
        this.selectedView = data?.saved_views?.find(v => v.id === viewId) || { ...body, id: viewId }
      } catch (error) {
        console.error('updateView error', error)
      }
    }
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useInventoryStore, import.meta.hot))
}